import React, { FunctionComponent, useEffect, useState } from 'react'

import i18next from 'i18next'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Text, TickCircleFilled, baseColors } from '@fairhq/common'
import { StatusTagWithRoundIcon } from 'components/StatusTagWithRoundIcon'
import { useCharlieUser } from 'hooks/useCharlieUser'
import { PracticeCriteriaWithLevelType } from 'store/reports/types'

import { insightsEqualityDetailsViewedEvent } from 'utils/analytics/amplitudeEvents'

import { TickThin } from './TickThin'

const Container = styled.section`
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid ${baseColors.borderBackground};
`

const Heading = styled.div`
  background-color: ${baseColors.backgroundCard};
  padding: 24px 40px;
  display: flex;
  gap: 40px;
  border-bottom: 1px solid ${baseColors.borderBackground};
`

const HeadingLeft = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 4px;
  min-width: 150px;
`

const PracticesContainer = styled.div<{ withoutPaddingRight: boolean }>`
  padding: ${({ withoutPaddingRight }) =>
    withoutPaddingRight ? `24px 24px 0 40px` : `24px 40px 0`};
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const PracticeRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`

const PracticeCard = styled.div`
  border-radius: 4px;
  background-color: ${baseColors.backgroundCard};
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 15px;
  width: 100%;
  border: 1px solid ${baseColors.borderBackground};
`

const TickIcon = styled(TickCircleFilled)`
  flex-shrink: 0;
`

const AccordionControl = styled.button<{ withTotBorder: boolean }>`
  width: 100%;
  text-align: center;
  line-height: 30px;
  padding: 12px;
  color: ${baseColors.contentContent};
  background-color: inherit;
  border-top: ${({ withTotBorder }) =>
    withTotBorder ? ` 1px solid ${baseColors.purple200}` : 'none'};
  &:focus-visible {
    outline: 2px solid ${baseColors.statesFocus};
    border-radius: 20px;
    outline-offset: -2px;
  }
`

const BenchmarkHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 28px;
`

const PeerLabel = styled(Text)`
  text-transform: uppercase;
  min-width: 50px;
  text-align: center;
`

const PeerDescription = styled(Text)`
  font-style: italic;
  line-height: 18px;
`

const PeerComparison = styled.div`
  min-width: 50px;
  text-align: center;
`

const AccordionText = styled(Text)`
  text-decoration: underline;
  &:hover {
    color: #7000cc;
    text-decoration-thickness: 3px;
  }
`

interface LevelCardProps {
  practices: PracticeCriteriaWithLevelType[]
  levelNumber: number
  isShowAllOpenInitialSate: boolean
  areaCode: string
}

export const LevelCard: FunctionComponent<LevelCardProps> = ({
  practices,
  levelNumber,
  isShowAllOpenInitialSate,
  areaCode,
}) => {
  const { t } = useTranslation()
  const charlieUser = useCharlieUser()

  const [isShowAllOpen, setIsShowAllOpen] = useState(false)

  const completionPercentage =
    (practices.filter(practice => practice.checked).length / practices.length) *
    100

  const showPeerComparison = !!practices.find(
    practice => practice.peerComparison
  )

  useEffect(() => {
    setIsShowAllOpen(isShowAllOpenInitialSate)
  }, [isShowAllOpenInitialSate])

  return (
    <Container>
      <Heading>
        <HeadingLeft>
          <Text type="body" element="h3" size="md" fontWeight={600}>
            <Trans i18nKey={`reports.level.level_${levelNumber}`} />
          </Text>
          {completionPercentage >= 0 && (
            <StatusTagWithRoundIcon percentage={completionPercentage} />
          )}
        </HeadingLeft>
        <Text type="body" element="p" size="sm" fontWeight={400}>
          <Trans i18nKey={`${areaCode}.${levelNumber}.description`} />
        </Text>
      </Heading>
      {practices.length > 0 && (
        <>
          {isShowAllOpen && (
            <PracticesContainer withoutPaddingRight={showPeerComparison}>
              {showPeerComparison && (
                <BenchmarkHeader>
                  <PeerDescription
                    type="body"
                    element="span"
                    size="2xs"
                    fontWeight={400}
                  >
                    {t(`reports.peerComparison.description`)}
                  </PeerDescription>
                  <PeerLabel
                    type="body"
                    element="span"
                    size="xs"
                    fontWeight={700}
                  >
                    {t(`reports.peerComparison.label`)}
                  </PeerLabel>
                </BenchmarkHeader>
              )}
              {practices.map(practice => (
                <PracticeRow key={practice.id}>
                  <PracticeCard>
                    {practice.checked ? (
                      <TickIcon fill={baseColors.contentContent} />
                    ) : (
                      <TickThin width="20" height="20" fill="transparent" />
                    )}

                    <Text type="body" element="p" size="sm" fontWeight={400}>
                      {practice.title}
                    </Text>
                  </PracticeCard>
                  {showPeerComparison && (
                    <PeerComparison>
                      <PeerLabel
                        type="body"
                        element="span"
                        size="xs"
                        fontWeight={700}
                      >
                        {practice.peerComparison &&
                          `${Math.round(practice.peerComparison)}%`}
                      </PeerLabel>
                    </PeerComparison>
                  )}
                </PracticeRow>
              ))}
            </PracticesContainer>
          )}
          <AccordionControl
            withTotBorder={!isShowAllOpen}
            onClick={() => {
              setIsShowAllOpen(!isShowAllOpen)
              insightsEqualityDetailsViewedEvent(
                { topic: i18next.t(`reports.level.level_${levelNumber}`) },
                charlieUser
              )
            }}
          >
            <AccordionText
              type="body"
              element="span"
              size="xs"
              fontWeight={500}
            >
              {isShowAllOpen
                ? t('reports.hideCriteria.label')
                : t('reports.showCriteria.label')}
            </AccordionText>
          </AccordionControl>
        </>
      )}
    </Container>
  )
}
